// ── Centralised EN / AF translations ─────────────────────────────────
// Add new keys as flat dot-less strings. Components call `t('key')`.
// The admin toggles the language in Settings; the choice is persisted
// in localStorage so it survives page reloads.

const translations = {
  en: {
    // ── Navigation ───────────────────────────────────────────
    nav_dashboard: 'Dashboard',
    nav_appointments: 'Appointments',
    nav_diary: 'Diary',
    nav_reminders: 'Reminders',
    nav_patients: 'Patients',
    nav_conversations: 'Conversations',
    nav_analytics: 'Analytics',
    nav_audit_log: 'Audit Log',
    nav_settings: 'Settings',
    nav_support: 'Support',
    nav_menu: 'Menu',
    nav_subtitle: 'AI Receptionist',

    // ── Dashboard ────────────────────────────────────────────
    greeting_morning: 'Good morning',
    greeting_afternoon: 'Good afternoon',
    greeting_evening: 'Good evening',
    dashboard_subtitle: "Here's what's happening at the practice today.",
    new_appointment: 'New Appointment',
    stat_total_patients: 'Total Patients',
    stat_new_this_month: 'new this month',
    stat_todays_appointments: "Today's Appointments",
    stat_confirmed: 'confirmed',
    stat_pending: 'pending',
    stat_new_patients: 'New Patients',
    stat_this_month: 'This month',
    stat_total_appointments: 'Total Appointments',
    stat_completed_today: 'completed today',
    chart_title: 'Appointment Overview',
    chart_subtitle: "This week's appointment breakdown",
    view_all: 'View all',
    see_all: 'See all',
    upcoming: 'Upcoming',
    consultation: 'Consultation',
    general_consultation: 'General consultation',
    todays_schedule: "Today's Schedule",
    appointments_count: 'appointments',
    no_appointments_today: 'No appointments scheduled for today',
    no_appointment_data: 'No appointment data for this week',
    no_upcoming: 'No upcoming appointments',
    reminders: 'Reminders',
    pending_label: 'pending',
    unconfirmed: 'Unconfirmed',
    call: 'Call',
    view: 'View',
    cancel_action: 'Cancel',
    reschedule_action: 'Reschedule',
    edit_patient: 'Edit patient',
    all_caught_up: 'All caught up — no pending reminders',
    recent_patients: 'Recent Patients',
    recent_patients_subtitle: 'Latest patients registered in the system',
    all_patients: 'All patients',
    th_patient: 'Patient',
    th_phone: 'Phone',
    th_email: 'Email',
    th_appointments: 'Appointments',
    th_last_visit: 'Last Visit',

    // ── Chart day labels ─────────────────────────────────────
    day_mon: 'Mon', day_tue: 'Tue', day_wed: 'Wed', day_thu: 'Thu',
    day_fri: 'Fri', day_sat: 'Sat', day_sun: 'Sun',

    // ── Chart legend ─────────────────────────────────────────
    chart_confirmed: 'Confirmed',
    chart_scheduled: 'Scheduled',
    chart_completed: 'Completed',
    chart_cancelled: 'Cancelled',

    // ── Status badges ────────────────────────────────────────
    status_scheduled: 'scheduled',
    status_confirmed: 'confirmed',
    status_completed: 'completed',
    status_cancelled: 'cancelled',
    status_no_show: 'no show',
    status_rescheduled: 'rescheduled',

    // ── Appointment Form Modal ───────────────────────────────
    modal_new_appointment: 'New Appointment',
    modal_edit_appointment: 'Edit Appointment',
    modal_cancel_btn: 'Cancel',
    modal_book_btn: 'Book appointment',
    modal_save_btn: 'Save changes',
    modal_patient_label: 'Patient',
    modal_select_patient: 'Select a patient\u2026',
    modal_start_time: 'Start time',
    modal_end_time: 'End time',
    modal_reason: 'Reason',
    modal_reason_placeholder: 'e.g. Root canal, cleaning, consultation',
    modal_notes: 'Notes',
    modal_notes_placeholder: 'Any additional context for the reception\u2026',
    modal_success_create: 'Appointment booked',
    modal_success_update: 'Appointment updated',
    modal_error_generic: 'Something went wrong',
    validation_patient_required: 'Patient is required',
    validation_start_required: 'Start time is required',
    validation_end_required: 'End time is required',
    validation_end_after_start: 'End time must be after start time',

    // ── Cancel Appointment Modal ─────────────────────────────
    cancel_title: 'Cancel Appointment',
    cancel_warning: "You're about to cancel the appointment for",
    cancel_irreversible: 'This action cannot be undone.',
    cancel_keep: 'Keep appointment',
    cancel_confirm: 'Confirm cancellation',
    cancel_submitting: 'Cancelling\u2026',
    cancel_reason_label: 'Reason category',
    cancel_reason_placeholder: 'Select a reason\u2026',
    cancel_notes_label: 'Additional notes',
    cancel_notes_optional: '(optional)',
    cancel_notes_placeholder: 'Anything the reception should know about this cancellation\u2026',
    cancel_success: 'Appointment cancelled',
    cancel_error: 'Could not cancel',
    cancel_pick_reason: 'Please pick a reason category',
    cancel_cause_label: 'Why isn\u2019t the patient coming?',
    cancel_cause_placeholder: 'Select why\u2026',
    cancel_pick_cause: 'Pick why the patient isn\u2019t coming (or \u2018Patient didn\u2019t say\u2019).',
    cancel_words_label: 'The patient\u2019s own words',
    cancel_words_placeholder: 'e.g. \u2018my medical aid only pays next year\u2019',
    cancel_words_required: 'Add the patient\u2019s own words for \u2018Other\u2019.',
    cancel_words_required_tag: '(required for Other)',
    cancel_cat_cost: 'Cost',
    cancel_cat_timing: 'Timing / schedule conflict',
    cancel_cat_fear: 'Fear / anxiety',
    cancel_cat_transport: 'Transport',
    cancel_cat_other: 'Other',

    // ── Settings ─────────────────────────────────────────────
    settings_title: 'Settings',
    settings_subtitle: 'Manage your practice configuration and preferences',
    settings_tab_practice: 'Practice Info',
    settings_tab_hours: 'Office Hours',
    settings_tab_notifications: 'Notifications',
    settings_tab_appearance: 'Appearance',
    settings_practice_info: 'Practice Details',
    settings_practice_info_desc: 'Your practice contact information and address',
    settings_practice_name: 'Practice Name',
    settings_practice_email: 'Email',
    settings_practice_phone: 'Phone',
    settings_practice_address: 'Address',
    settings_view_on_map: 'View on Google Maps',
    settings_language: 'Language',
    settings_language_desc: 'Choose the display language for the dashboard',
    settings_office_hours: 'Office Hours',
    settings_office_hours_desc: 'Weekly schedule for the practice',
    settings_pricing: 'Pricing',
    settings_pricing_desc: 'Treatment prices shown to patients',
    settings_cash_practice_note: 'We are a cash practice — we accept cash and card payments. We do not claim from medical aids directly. After payment we issue a statement for the patient to claim back.',
    settings_faq: 'FAQ Knowledge Base',
    settings_day: 'Day',
    settings_hours: 'Hours',
    settings_break: 'Break',
    settings_status: 'Status',
    settings_open: 'Open',
    settings_closed: 'Closed',
    settings_all_other: 'All other treatments',
    settings_requires_consult: 'Requires consultation',
    settings_notif_title: 'Notification Channels',
    settings_notif_subtitle: 'Configure how appointment confirmations and reminders are sent',
    settings_notif_email: 'Email',
    settings_notif_confirmations: 'Confirmations',
    settings_notif_reminders: 'Reminders',
    settings_notif_both_active: 'Confirmations and reminders active',
    settings_notif_confirm_only: 'Confirmations only',
    settings_notif_remind_only: 'Reminders only',
    settings_notif_not_configured: 'Not configured',
    settings_notif_reminder_timing: 'Reminder Timing',
    settings_notif_reminder_timing_desc: 'How far before the appointment reminders are sent',

    // ── Reminders page ──────────────────────────────────────
    rem_badge: 'Follow-up queue',
    rem_title: 'Pre-Appointment Reminders',
    rem_subtitle: 'Track all upcoming appointments and their confirmation status. Send reminders, confirm, or cancel directly from this page.',
    rem_total_upcoming: 'Total upcoming',
    rem_pending: 'Pending',
    rem_confirmed: 'Confirmed',
    rem_today: 'Today',
    rem_tomorrow: 'Tomorrow',
    rem_this_week: 'This Week',
    rem_search: 'Search patient, phone, status\u2026',
    rem_all_caught_up: 'All caught up',
    rem_no_match: 'No appointments match your current filter.',
    rem_th_patient: 'Patient',
    rem_th_appointment: 'Appointment',
    rem_th_reason: 'Reason',
    rem_th_status: 'Status',
    rem_th_phone: 'Phone',
    rem_th_actions: 'Actions',
    rem_send_whatsapp: 'Send WhatsApp',
    rem_call: 'Call',
    rem_confirm: 'Confirm',
    rem_cancel: 'Cancel',
    rem_general_appointment: 'General appointment',
    rem_showing: 'Showing',
    rem_to: 'to',
    rem_of: 'of',
    rem_results: 'results',
    rem_m_away: 'm away',
    rem_h_away: 'h away',
    rem_sent_success: 'reminder sent to',
    rem_send_error: 'Could not send reminder',
    rem_confirmed_success: 'confirmed',
    rem_confirm_error: 'Could not confirm',
    chip_pending: 'Pending',
    chip_sent: 'Sent',
    chip_confirmed: 'Confirmed',
    chip_cancelled: 'Cancelled',
    chip_no_answer: 'No Answer',
    chip_completed: 'Completed',
    chip_no_show: 'No Show',
    chip_rescheduled: 'Rescheduled',

    // ── Patients page ───────────────────────────────────────
    pat_badge: 'Patient records',
    pat_title: 'Patients',
    pat_registered: 'registered patients',
    pat_new: 'New Patient',
    pat_total: 'Total Patients',
    pat_active: 'Active',
    pat_new_month: 'New This Month',
    pat_needs_review: 'Needs Review',
    pat_search: 'Search name, phone, email\u2026',
    pat_total_label: 'patients',
    pat_empty: 'No patients registered yet',
    pat_all_statuses: 'All statuses',
    pat_clear: 'Clear',
    pat_col_id: 'ID No.',
    pat_col_name: 'Patient Name',
    pat_col_status: 'Status',
    pat_col_due: 'Due',
    pat_col_phone: 'Phone',
    pat_col_age: 'Age',
    pat_col_next: 'Next Appointment',
    pat_col_action: 'Action',
    pat_status_active: 'active',
    pat_status_inactive: 'inactive',

    // ── Appointments page ───────────────────────────────────
    apt_badge: 'Schedule view',
    apt_title: 'Appointments',
    apt_subtitle_suffix: 'appointments tracked across the live clinic diary.',
    apt_schedule: 'Schedule',
    apt_list: 'List',
    apt_new: 'New Appointment',
    apt_live_count: 'Live count',
    apt_search: 'Search patient, phone, reason\u2026',
    apt_total_label: 'appointments',
    apt_empty: 'No appointments found',
    apt_all_statuses: 'All statuses',
    apt_clear: 'Clear',
    apt_col_patient: 'Patient',
    apt_col_datetime: 'Date & Time',
    apt_col_reason: 'Reason',
    apt_col_status: 'Status',
    apt_col_actions: 'Actions',

    // ── Conversations page ──────────────────────────────────
    conv_badge: 'Communication hub',
    conv_title: 'Conversations',
    conv_subtitle: 'WhatsApp and voice call transcripts',
    conv_import: 'Import chats',
    conv_search: 'Search customer, phone or topic\u2026',
    conv_all_sources: 'All sources',
    conv_live: 'Live',
    conv_imported: 'Imported',
    conv_all_channels: 'All channels',
    conv_all_tags: 'All tags',
    conv_whatsapp: 'WhatsApp',
    conv_voice: 'Voice',
    conv_col_customer: 'Customer',
    conv_col_topic: 'Topic / last message',
    conv_col_when: 'When',
    conv_no_match: 'No conversations match.',
    conv_no_messages: 'No messages',
    conv_showing: 'Showing',
    conv_msgs: 'msgs',
    conv_import_title: 'Import historical chats',
    conv_import_file_label: 'Export file (.json preferred, .txt supported)',
    conv_import_file_hint: 'JSON should be an array of threads with',
    conv_import_owner_label: 'Owner (clinic) name as it appears in the export',
    conv_import_phone_label: 'Patient phone (E.164, e.g. +27831234567)',
    conv_import_cancel: 'Cancel',
    conv_import_importing: 'Importing\u2026',
    conv_import_btn: 'Import',
    conv_import_choose: 'Please choose a file to import',
    conv_import_error: 'Import failed',
    conv_just_now: 'just now',
    conv_m_ago: 'm ago',
    conv_h_ago: 'h ago',
    conv_d_ago: 'd ago',

    // ── Analytics page ──────────────────────────────────────
    ana_badge: 'Practice analytics',
    ana_title: 'Analytics',
    ana_subtitle: 'Cancellation reasons, booking stats, and channel performance',
    ana_booking_title: 'Booking Stats \u2014 Last 30 Days',
    ana_total_bookings: 'Total Bookings',
    ana_completed: 'Completed',
    ana_no_shows: 'No Shows',
    ana_conversion: 'Conversion Rate',
    ana_channel_title: 'Channel Performance',
    ana_whatsapp_conv: 'WhatsApp Conversations',
    ana_voice_conv: 'Voice Conversations',
    ana_pct_total: '% of total',
    ana_cancel_title: 'Cancellation Reasons',
    ana_total_cancelled: 'Total cancelled:',
    ana_rate: 'Rate:',
  },

  af: {
    // ── Navigation ───────────────────────────────────────────
    nav_dashboard: 'Paneelbord',
    nav_appointments: 'Afsprake',
    nav_diary: 'Dagboek',
    nav_reminders: 'Herinnerings',
    nav_patients: 'Pasi\u00ebnte',
    nav_conversations: 'Gesprekke',
    nav_analytics: 'Analitiek',
    nav_audit_log: 'Ouditlys',
    nav_settings: 'Instellings',
    nav_support: 'Ondersteuning',
    nav_menu: 'Kieslys',
    nav_subtitle: 'KI Ontvangsdame',

    // ── Dashboard ────────────────────────────────────────────
    greeting_morning: 'Goeie m\u00f4re',
    greeting_afternoon: 'Goeie middag',
    greeting_evening: 'Goeie aand',
    dashboard_subtitle: 'Hier is wat vandag by die praktyk gebeur.',
    new_appointment: 'Nuwe Afspraak',
    stat_total_patients: 'Totale Pasi\u00ebnte',
    stat_new_this_month: 'nuut hierdie maand',
    stat_todays_appointments: 'Vandag se Afsprake',
    stat_confirmed: 'bevestig',
    stat_pending: 'hangende',
    stat_new_patients: 'Nuwe Pasi\u00ebnte',
    stat_this_month: 'Hierdie maand',
    stat_total_appointments: 'Totale Afsprake',
    stat_completed_today: 'vandag voltooi',
    chart_title: 'Afspraak Oorsig',
    chart_subtitle: 'Hierdie week se afspraak-uiteensetting',
    view_all: 'Sien alles',
    see_all: 'Sien alles',
    upcoming: 'Opkomend',
    consultation: 'Konsultasie',
    general_consultation: 'Algemene konsultasie',
    todays_schedule: 'Vandag se Skedule',
    appointments_count: 'afsprake',
    no_appointments_today: 'Geen afsprake vir vandag geskeduleer nie',
    no_appointment_data: 'Geen afspraakdata vir hierdie week nie',
    no_upcoming: 'Geen opkomende afsprake nie',
    reminders: 'Herinnerings',
    pending_label: 'hangende',
    unconfirmed: 'Onbevestig',
    call: 'Bel',
    view: 'Besigtig',
    cancel_action: 'Kanselleer',
    reschedule_action: 'Herskeduleer',
    edit_patient: 'Wysig pasiënt',
    all_caught_up: 'Alles op datum \u2014 geen hangende herinnerings nie',
    recent_patients: 'Onlangse Pasi\u00ebnte',
    recent_patients_subtitle: 'Nuutste pasi\u00ebnte geregistreer in die stelsel',
    all_patients: 'Alle pasi\u00ebnte',
    th_patient: 'Pasi\u00ebnt',
    th_phone: 'Telefoon',
    th_email: 'E-pos',
    th_appointments: 'Afsprake',
    th_last_visit: 'Laaste Besoek',

    // ── Chart day labels ─────────────────────────────────────
    day_mon: 'Ma', day_tue: 'Di', day_wed: 'Wo', day_thu: 'Do',
    day_fri: 'Vr', day_sat: 'Sa', day_sun: 'So',

    // ── Chart legend ─────────────────────────────────────────
    chart_confirmed: 'Bevestig',
    chart_scheduled: 'Geskeduleer',
    chart_completed: 'Voltooi',
    chart_cancelled: 'Gekanselleer',

    // ── Status badges ────────────────────────────────────────
    status_scheduled: 'geskeduleer',
    status_confirmed: 'bevestig',
    status_completed: 'voltooi',
    status_cancelled: 'gekanselleer',
    status_no_show: 'nie opgedaag',
    status_rescheduled: 'herskeduleer',

    // ── Appointment Form Modal ───────────────────────────────
    modal_new_appointment: 'Nuwe Afspraak',
    modal_edit_appointment: 'Wysig Afspraak',
    modal_cancel_btn: 'Terug',
    modal_book_btn: 'Bespreek afspraak',
    modal_save_btn: 'Stoor veranderinge',
    modal_patient_label: 'Pasi\u00ebnt',
    modal_select_patient: 'Kies \'n pasi\u00ebnt\u2026',
    modal_start_time: 'Begintyd',
    modal_end_time: 'Eindtyd',
    modal_reason: 'Rede',
    modal_reason_placeholder: 'bv. Wortelkanaal, skoonmaak, konsultasie',
    modal_notes: 'Notas',
    modal_notes_placeholder: 'Enige bykomende konteks vir die ontvangs\u2026',
    modal_success_create: 'Afspraak bespreek',
    modal_success_update: 'Afspraak opgedateer',
    modal_error_generic: 'Iets het fout gegaan',
    validation_patient_required: 'Pasi\u00ebnt is verpligtend',
    validation_start_required: 'Begintyd is verpligtend',
    validation_end_required: 'Eindtyd is verpligtend',
    validation_end_after_start: 'Eindtyd moet na begintyd wees',

    // ── Cancel Appointment Modal ─────────────────────────────
    cancel_title: 'Kanselleer Afspraak',
    cancel_warning: 'Jy is op die punt om die afspraak te kanselleer vir',
    cancel_irreversible: 'Hierdie aksie kan nie ongedaan gemaak word nie.',
    cancel_keep: 'Hou afspraak',
    cancel_confirm: 'Bevestig kansellering',
    cancel_submitting: 'Besig om te kanselleer\u2026',
    cancel_reason_label: 'Rede kategorie',
    cancel_reason_placeholder: 'Kies \'n rede\u2026',
    cancel_notes_label: 'Bykomende notas',
    cancel_notes_optional: '(opsioneel)',
    cancel_notes_placeholder: 'Enigiets wat die ontvangs moet weet oor hierdie kansellering\u2026',
    cancel_success: 'Afspraak gekanselleer',
    cancel_error: 'Kon nie kanselleer nie',
    cancel_pick_reason: 'Kies asseblief \'n rede kategorie',
    cancel_cause_label: 'Hoekom kom die pasi\u00ebnt nie?',
    cancel_cause_placeholder: 'Kies hoekom\u2026',
    cancel_pick_cause: 'Kies hoekom die pasi\u00ebnt nie kom nie (of \u2018Pasi\u00ebnt het nie ges\u00ea nie\u2019).',
    cancel_words_label: 'Die pasi\u00ebnt se eie woorde',
    cancel_words_placeholder: 'bv. \u2018my mediese fonds betaal eers volgende jaar\u2019',
    cancel_words_required: 'Voeg die pasi\u00ebnt se eie woorde by vir \u2018Ander\u2019.',
    cancel_words_required_tag: '(verpligtend vir Ander)',
    cancel_cat_cost: 'Koste',
    cancel_cat_timing: 'Tyd / skedule konflik',
    cancel_cat_fear: 'Vrees / angs',
    cancel_cat_transport: 'Vervoer',
    cancel_cat_other: 'Ander',

    // ── Settings ─────────────────────────────────────────────
    settings_title: 'Instellings',
    settings_subtitle: 'Bestuur jou praktyk-konfigurasie en voorkeure',
    settings_tab_practice: 'Praktyk-inligting',
    settings_tab_hours: 'Kantoorure',
    settings_tab_notifications: 'Kennisgewings',
    settings_tab_appearance: 'Voorkoms',
    settings_practice_info: 'Praktykbesonderhede',
    settings_practice_info_desc: 'Jou praktyk se kontakinligting en adres',
    settings_practice_name: 'Praktyknaam',
    settings_practice_email: 'E-pos',
    settings_practice_phone: 'Telefoon',
    settings_practice_address: 'Adres',
    settings_view_on_map: 'Sien op Google Maps',
    settings_language: 'Taal',
    settings_language_desc: 'Kies die vertoontaal vir die paneelbord',
    settings_office_hours: 'Kantoorure',
    settings_office_hours_desc: 'Weeklikse skedule vir die praktyk',
    settings_pricing: 'Pryse',
    settings_pricing_desc: 'Behandelingspryse wat aan pasi\u00ebnte gewys word',
    settings_cash_practice_note: 'Ons is \'n kontantpraktyk \u2014 ons aanvaar kontant en kaartbetalings. Ons eis nie direk van mediese fondse nie. Na betaling reik ons \'n staat uit vir die pasi\u00ebnt om terug te eis.',
    settings_faq: 'FAQ Kennisbasis',
    settings_day: 'Dag',
    settings_hours: 'Ure',
    settings_break: 'Pouse',
    settings_status: 'Status',
    settings_open: 'Oop',
    settings_closed: 'Gesluit',
    settings_all_other: 'Alle ander behandelinge',
    settings_requires_consult: 'Vereis konsultasie',
    settings_notif_title: 'Kennisgewingkanale',
    settings_notif_subtitle: 'Stel op hoe afspraakbevestigings en herinnerings gestuur word',
    settings_notif_email: 'E-pos',
    settings_notif_confirmations: 'Bevestigings',
    settings_notif_reminders: 'Herinnerings',
    settings_notif_both_active: 'Bevestigings en herinnerings aktief',
    settings_notif_confirm_only: 'Slegs bevestigings',
    settings_notif_remind_only: 'Slegs herinnerings',
    settings_notif_not_configured: 'Nie opgestel nie',
    settings_notif_reminder_timing: 'Herinnerings-tydsberekening',
    settings_notif_reminder_timing_desc: 'Hoeveel tyd voor die afspraak herinnerings gestuur word',

    // ── Reminders page ──────────────────────────────────────
    rem_badge: 'Opvolg-waglys',
    rem_title: 'Vorafspraak-herinnerings',
    rem_subtitle: 'Volg alle opkomende afsprake en hul bevestigingstatus na. Stuur herinnerings, bevestig, of kanselleer direk vanaf hierdie bladsy.',
    rem_total_upcoming: 'Totaal opkomend',
    rem_pending: 'Hangende',
    rem_confirmed: 'Bevestig',
    rem_today: 'Vandag',
    rem_tomorrow: 'M\u00f4re',
    rem_this_week: 'Hierdie Week',
    rem_search: 'Soek pasi\u00ebnt, telefoon, status\u2026',
    rem_all_caught_up: 'Alles op datum',
    rem_no_match: 'Geen afsprake pas by jou huidige filter nie.',
    rem_th_patient: 'Pasi\u00ebnt',
    rem_th_appointment: 'Afspraak',
    rem_th_reason: 'Rede',
    rem_th_status: 'Status',
    rem_th_phone: 'Telefoon',
    rem_th_actions: 'Aksies',
    rem_send_whatsapp: 'Stuur WhatsApp',
    rem_call: 'Bel',
    rem_confirm: 'Bevestig',
    rem_cancel: 'Kanselleer',
    rem_general_appointment: 'Algemene afspraak',
    rem_showing: 'Wys',
    rem_to: 'tot',
    rem_of: 'van',
    rem_results: 'resultate',
    rem_m_away: 'm oor',
    rem_h_away: 'u oor',
    rem_sent_success: 'herinnering gestuur na',
    rem_send_error: 'Kon nie herinnering stuur nie',
    rem_confirmed_success: 'bevestig',
    rem_confirm_error: 'Kon nie bevestig nie',
    chip_pending: 'Hangende',
    chip_sent: 'Gestuur',
    chip_confirmed: 'Bevestig',
    chip_cancelled: 'Gekanselleer',
    chip_no_answer: 'Geen Antwoord',
    chip_completed: 'Voltooi',
    chip_no_show: 'Nie Opgedaag',
    chip_rescheduled: 'Herskeduleer',

    // ── Patients page ───────────────────────────────────────
    pat_badge: 'Pasi\u00ebntrekords',
    pat_title: 'Pasi\u00ebnte',
    pat_registered: 'geregistreerde pasi\u00ebnte',
    pat_new: 'Nuwe Pasi\u00ebnt',
    pat_total: 'Totale Pasi\u00ebnte',
    pat_active: 'Aktief',
    pat_new_month: 'Nuut Hierdie Maand',
    pat_needs_review: 'Moet Hersien',
    pat_search: 'Soek naam, telefoon, e-pos\u2026',
    pat_total_label: 'pasi\u00ebnte',
    pat_empty: 'Geen pasi\u00ebnte geregistreer nog nie',
    pat_all_statuses: 'Alle statusse',
    pat_clear: 'Maak skoon',
    pat_col_id: 'ID Nr.',
    pat_col_name: 'Pasi\u00ebntnaam',
    pat_col_status: 'Status',
    pat_col_due: 'Verskuldig',
    pat_col_phone: 'Telefoon',
    pat_col_age: 'Ouderdom',
    pat_col_next: 'Volgende Afspraak',
    pat_col_action: 'Aksie',
    pat_status_active: 'aktief',
    pat_status_inactive: 'onaktief',

    // ── Appointments page ───────────────────────────────────
    apt_badge: 'Skedule-aansig',
    apt_title: 'Afsprake',
    apt_subtitle_suffix: 'afsprake opgespoor in die lewendige kliniekdagboek.',
    apt_schedule: 'Skedule',
    apt_list: 'Lys',
    apt_new: 'Nuwe Afspraak',
    apt_live_count: 'Lewendige telling',
    apt_search: 'Soek pasi\u00ebnt, telefoon, rede\u2026',
    apt_total_label: 'afsprake',
    apt_empty: 'Geen afsprake gevind nie',
    apt_all_statuses: 'Alle statusse',
    apt_clear: 'Maak skoon',
    apt_col_patient: 'Pasi\u00ebnt',
    apt_col_datetime: 'Datum & Tyd',
    apt_col_reason: 'Rede',
    apt_col_status: 'Status',
    apt_col_actions: 'Aksies',

    // ── Conversations page ──────────────────────────────────
    conv_badge: 'Kommunikasie-spil',
    conv_title: 'Gesprekke',
    conv_subtitle: 'WhatsApp en stemoproep-transkripsies',
    conv_import: 'Voer kletse in',
    conv_search: 'Soek kli\u00ebnt, telefoon of onderwerp\u2026',
    conv_all_sources: 'Alle bronne',
    conv_live: 'Lewendig',
    conv_imported: 'Ingevoer',
    conv_all_channels: 'Alle kanale',
    conv_all_tags: 'Alle etikette',
    conv_whatsapp: 'WhatsApp',
    conv_voice: 'Stem',
    conv_col_customer: 'Kli\u00ebnt',
    conv_col_topic: 'Onderwerp / laaste boodskap',
    conv_col_when: 'Wanneer',
    conv_no_match: 'Geen gesprekke pas nie.',
    conv_no_messages: 'Geen boodskappe',
    conv_showing: 'Wys',
    conv_msgs: 'bsks',
    conv_import_title: 'Voer historiese kletse in',
    conv_import_file_label: 'Uitvoer-l\u00eaer (.json verkies, .txt ondersteun)',
    conv_import_file_hint: 'JSON moet \'n reeks drade wees met',
    conv_import_owner_label: 'Eienaar (kliniek) naam soos dit in die uitvoer verskyn',
    conv_import_phone_label: 'Pasi\u00ebnt telefoon (E.164, bv. +27831234567)',
    conv_import_cancel: 'Terug',
    conv_import_importing: 'Besig om in te voer\u2026',
    conv_import_btn: 'Voer in',
    conv_import_choose: 'Kies asseblief \'n l\u00eaer om in te voer',
    conv_import_error: 'Invoer het misluk',
    conv_just_now: 'pas nou',
    conv_m_ago: 'm gelede',
    conv_h_ago: 'u gelede',
    conv_d_ago: 'd gelede',

    // ── Analytics page ──────────────────────────────────────
    ana_badge: 'Praktyk-analitiek',
    ana_title: 'Analitiek',
    ana_subtitle: 'Kansellasie-redes, besprekingstatistieke, en kanaalprestasie',
    ana_booking_title: 'Besprekingstatistieke \u2014 Laaste 30 Dae',
    ana_total_bookings: 'Totale Besprekings',
    ana_completed: 'Voltooi',
    ana_no_shows: 'Nie Opgedaag',
    ana_conversion: 'Omsettingskoers',
    ana_channel_title: 'Kanaalprestasie',
    ana_whatsapp_conv: 'WhatsApp Gesprekke',
    ana_voice_conv: 'Stemgesprekke',
    ana_pct_total: '% van totaal',
    ana_cancel_title: 'Kansellasie-redes',
    ana_total_cancelled: 'Totaal gekanselleer:',
    ana_rate: 'Koers:',
  },
}

export default translations
